import React from "react";

import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import "./segmented-control.scss";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";

function isIntegerString(value: string): boolean {
  return /^-?\d+$/.test(value.trim());
}

type SegmentedControlValue = string | number;

export interface SegmentedControlTab<T extends SegmentedControlValue = number> {
  label: string;
  value: T;
}

interface SegmentedControlProps<T extends SegmentedControlValue = number> {
  disabled?: boolean;
  onChange: (value: T) => void;
  selected?: T;
  tabs: SegmentedControlTab<T>[];
}

function SegmentedControl<T extends SegmentedControlValue = number>({
  disabled,
  onChange,
  selected = 0 as T,
  tabs,
}: SegmentedControlProps<T>): React.ReactElement {
  const { t } = useTranslation("editor-platform");

  return (
    <Tabs
      className="mb-1 h-10"
      value={selected.toString()}
      onValueChange={(value) => {
        if (isIntegerString(value)) {
          onChange(parseInt(value, 10) as T);
        } else {
          onChange(value as T);
        }
      }}
    >
      <TabsList className="h-10! gap-3">
        {tabs.map((tab) => (
          <TabsTrigger
            key={uuidv4()}
            className="data-[state=active]:text-primary px-6 py-3"
            disabled={disabled}
            value={tab?.value.toString()}
          >
            {t(tab.label)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export default SegmentedControl;
